import type { SupabaseClient } from '@supabase/supabase-js';

import { Result, ServiceError } from '@kit/shared/result';
import { BaseService } from '@kit/shared/services';
import type { Database } from '@kit/supabase/database';

import { DEFAULT_FEATURE_FLAGS } from '../lib/defaults';
import { FeatureFlagMapper } from '../mappers/feature-flag.mapper';
import type { FeatureFlag, FeatureFlagRow, FeatureFlagsConfig } from '../types';

/**
 * Service for managing feature flags
 * Provides database-backed feature flag storage with caching
 */
export class FeatureFlagService extends BaseService<Database> {
  constructor(client: SupabaseClient<Database>) {
    super(client);
  }

  /**
   * Get all active feature flags
   */
  async getAllFlags(): Promise<Result<FeatureFlag[]>> {
    try {
      const { data, error } = await this.client
        .from('feature_flags')
        .select('*')
        .eq('is_active', true)
        .order('category', { ascending: true })
        .order('key', { ascending: true });

      if (error) {
        await this.log('error', 'Failed to fetch feature flags', { error });
        return Result.fail(this.handleDatabaseError(error));
      }

      const flags = (data || []).map((row) => FeatureFlagMapper.toEntity(row));
      return Result.ok(flags);
    } catch (error: unknown) {
      return Result.fail(
        new ServiceError(
          'INTERNAL_ERROR',
          'Failed to fetch feature flags',
          error,
        ),
      );
    }
  }

  /**
   * Get typed config object (for compatibility with existing code)
   * Falls back to defaults if database is unavailable
   */
  async getConfig(): Promise<FeatureFlagsConfig> {
    const flagsResult = await this.getAllFlags();

    if (flagsResult.isFailure) {
      await this.log(
        'warn',
        'Failed to load flags from DB, using defaults',
        {},
      );
      return DEFAULT_FEATURE_FLAGS;
    }

    const flags = flagsResult.getValue();
    return this.buildConfigFromFlags(flags);
  }

  /**
   * Get a single flag by key.
   *
   * Kept deliberately as part of the service's public contract: the
   * feature-flag e2e suite (apps/web/app/__tests__/e2e/feature-flags)
   * and unit tests exercise per-key evaluation through this method, so
   * it is a supported lookup surface even though no shipping page reads
   * a single flag directly today (callers use getConfig()/getAllFlags()).
   */
  async getFlagByKey(key: string): Promise<Result<FeatureFlag>> {
    try {
      const { data, error } = await this.client
        .from('feature_flags')
        .select('*')
        .eq('key', key)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          return Result.fail(
            new ServiceError('NOT_FOUND', 'Feature flag not found'),
          );
        }
        return Result.fail(this.handleDatabaseError(error));
      }

      return Result.ok(FeatureFlagMapper.toEntity(data));
    } catch (error: unknown) {
      return Result.fail(
        new ServiceError('INTERNAL_ERROR', 'Failed to fetch flag', error),
      );
    }
  }

  /**
   * Update a feature flag value (super admin only - RLS enforced)
   */
  async updateFlag(
    id: string,
    updates: { boolValue?: boolean; stringValue?: string },
  ): Promise<Result<FeatureFlag>> {
    try {
      await this.log('info', 'Updating feature flag', { id });

      const updateData: Partial<FeatureFlagRow> = {};
      if (updates.boolValue !== undefined) {
        updateData.bool_value = updates.boolValue;
      }
      if (updates.stringValue !== undefined) {
        updateData.string_value = updates.stringValue;
      }

      const { data, error } = await this.client
        .from('feature_flags')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        await this.log('error', 'Failed to update feature flag', {
          id,
          error,
        });
        return Result.fail(this.handleDatabaseError(error));
      }

      await this.log('info', 'Feature flag updated', { id });
      return Result.ok(FeatureFlagMapper.toEntity(data));
    } catch (error: unknown) {
      return Result.fail(
        new ServiceError('INTERNAL_ERROR', 'Failed to update flag', error),
      );
    }
  }

  /**
   * Build config object from flag array
   */
  private buildConfigFromFlags(flags: FeatureFlag[]): FeatureFlagsConfig {
    const flagMap = new Map(flags.map((f) => [f.key, f]));

    const languagePriorityValue = flagMap.get('languagePriority')?.stringValue;
    const languagePriority =
      languagePriorityValue === 'user' ||
      languagePriorityValue === 'application'
        ? languagePriorityValue
        : DEFAULT_FEATURE_FLAGS.languagePriority;

    return {
      enableThemeToggle:
        flagMap.get('enableThemeToggle')?.boolValue ??
        DEFAULT_FEATURE_FLAGS.enableThemeToggle,
      enableAccountDeletion:
        flagMap.get('enableAccountDeletion')?.boolValue ??
        DEFAULT_FEATURE_FLAGS.enableAccountDeletion,
      enableTeamDeletion:
        flagMap.get('enableTeamDeletion')?.boolValue ??
        DEFAULT_FEATURE_FLAGS.enableTeamDeletion,
      enableTeamAccounts:
        flagMap.get('enableTeamAccounts')?.boolValue ??
        DEFAULT_FEATURE_FLAGS.enableTeamAccounts,
      enableTeamCreation:
        flagMap.get('enableTeamCreation')?.boolValue ??
        DEFAULT_FEATURE_FLAGS.enableTeamCreation,
      enablePersonalAccountBilling:
        flagMap.get('enablePersonalAccountBilling')?.boolValue ??
        DEFAULT_FEATURE_FLAGS.enablePersonalAccountBilling,
      enableTeamAccountBilling:
        flagMap.get('enableTeamAccountBilling')?.boolValue ??
        DEFAULT_FEATURE_FLAGS.enableTeamAccountBilling,
      languagePriority,
      enableNotifications:
        flagMap.get('enableNotifications')?.boolValue ??
        DEFAULT_FEATURE_FLAGS.enableNotifications,
      realtimeNotifications:
        flagMap.get('realtimeNotifications')?.boolValue ??
        DEFAULT_FEATURE_FLAGS.realtimeNotifications,
      enableVersionUpdater:
        flagMap.get('enableVersionUpdater')?.boolValue ??
        DEFAULT_FEATURE_FLAGS.enableVersionUpdater,
      enableSavedSignatures:
        flagMap.get('enableSavedSignatures')?.boolValue ??
        DEFAULT_FEATURE_FLAGS.enableSavedSignatures,
      enableCoaching:
        flagMap.get('enableCoaching')?.boolValue ??
        DEFAULT_FEATURE_FLAGS.enableCoaching,
      enableAmbassadors:
        flagMap.get('enableAmbassadors')?.boolValue ??
        DEFAULT_FEATURE_FLAGS.enableAmbassadors,
    };
  }
}

/**
 * Factory function to create FeatureFlagService
 */
export function createFeatureFlagService(
  client: SupabaseClient<Database>,
): FeatureFlagService {
  return new FeatureFlagService(client);
}
